import { Injectable } from '@angular/core';
import { PRIMARY_OUTLET, Router } from '@angular/router';
import { Location } from '@angular/common';
import { IPageDetails } from '../data/page-details';
import { IFormTypes } from '../data/form-types';
import { INavigationService } from './Inavigation-service';
import { FormTypes } from '../constants/form-types.constants';
import { AssessmentService } from './assessment.service';


@Injectable({
  providedIn: 'root'
})
export class NavigationService implements INavigationService {
  // Use FormTypes constants for better type safety and consistency
  private readonly formTypes = FormTypes.getAll();

  selectedRoute: IPageDetails | null = null;

  constructor(
    private router: Router,
    private location: Location,
    private assessmentService: AssessmentService
  ) { }

  private getRoutes(): IPageDetails[] {
    return [
      { id: FormTypes.BUSINESS, pages: ['applicant details', 'education', 'business skills', 'business investments', 'other family', 'health and character', 'finish'] },
      { id: FormTypes.CHILD, pages: ['applicant details', 'sponsor details', 'other family', 'health and character', 'finish'] },
      { id: FormTypes.PARENT, pages: ['applicant details', 'sponsor details', 'other family', 'health and character', 'finish'] },
      { id: FormTypes.PARTNER, pages: ['applicant details', 'sponsor partner', 'relationship partner', 'other family', 'health and character', 'finish'] },
      { id: FormTypes.SKILLED, pages: ['applicant details', 'employer details', 'education', 'occupation', 'other family', 'health and character', 'finish'] },
      { id: FormTypes.STUDY, pages: ['applicant details', 'education', 'other family', 'health and character', 'finish'] },
      { id: FormTypes.WORK, pages: ['applicant details', 'employer details', 'education', 'occupation', 'other family', 'health and character', 'finish'] },
      { id: FormTypes.EMPLOYER_TEMP, pages: ['employer sponsor', 'positions', 'finish'] },
      { id: FormTypes.EMPLOYER_PERM, pages: ['employer sponsor', 'positions', 'finish'] },
      { id: FormTypes.GENERAL, pages: ['enquiry'] },
      { id: FormTypes.BUSINESS_ENQUIRY, pages: ['business enquiry'] },
      { id: FormTypes.APPEAL_REFUSE, pages: ['applicant details', 'decision', 'finish'] },
      { id: FormTypes.APPEAL_CANCEL, pages: ['applicant details', 'decision', 'finish'] },
      { id: FormTypes.VISIT, pages: ['applicant details', 'sponsor details', 'occupation', 'other family', 'health and character', 'finish'] },
      { id: FormTypes.GENERIC, pages: ['general enquiry'] },
      { id: FormTypes.VEVO_MYSELF, pages: ['applicant details', 'employer details', 'other family', 'finish'] },
      { id: FormTypes.VEVO_EMPLOYEE, pages: ['applicant details', 'employer details', 'other family', 'finish'] },
      { id: FormTypes.VEVO_CONTRACT, pages: ['applicant details', 'employer details', 'other family', 'finish'] }
    ];
  }

  setSelectedRoute(formType: string): void {
    this.selectedRoute = this.getRoutesFor(formType);
    this.assessmentService.selectedForm.set(this.selectedRoute);
  }

  getSelectedRoute(): IPageDetails {
    return this.selectedRoute!;
  }

  private getRoutesFor(formType: string): IPageDetails {
    const routes = this.getRoutes();
    const route = routes.find(r => r.id === formType);
    
    if (!route) {
      throw new Error('Invalid form type');
    }
    
    return route;
  }

  getFormTypes(): IFormTypes {
    return this.formTypes;
  }

  private convertPageNameToRoute(name: string): string {
    return name ? name.replace(/\s/g, '-') : '';
  }

  redirectToHome(): void {
    this.router.navigate(['/assessment']);
  }

  redirectToThankyou(): void {
    this.router.navigate([`/assessment/thank-you`]);
  }

  routeIsGeneralEnquiry(): boolean {
    const segments = this.router.parseUrl(this.router.url).root.children[PRIMARY_OUTLET]?.segments ?? [];
    const path = segments.map(s => s.path.toLowerCase()).join('/');
    return path === 'assessment/enquiry/general-enquiry';
  }

  navigateToPage(index: number): void {
    if (this.selectedRoute) {
      const routePath = `/assessment/${this.selectedRoute.id}/${this.convertPageNameToRoute(this.selectedRoute.pages[index])}`;
      this.router.navigate([routePath]);
    }
  }

  getPageIndex(page: string): number {
    return this.selectedRoute
      ? this.selectedRoute.pages.indexOf(page.replace(/-/g, ' '))
      : -1;
  }

  isSelectedRouteFor(formType: string): boolean {
    return this.selectedRoute != null && this.selectedRoute.id === formType;
  }

  // Additional helper methods for Angular 18
  getCurrentUrl(): string {
    return this.router.url;
  }

  getCurrentPath(): string {
    return this.location.path();
  }

  // Navigate with query parameters if needed
  navigateWithParams(route: string[], params?: any): void {
    this.router.navigate(route, { queryParams: params });
  }

  // Check if current route matches a pattern
  isCurrentRoute(route: string): boolean {
    return this.router.url.includes(route);
  }
}